/* cf-si-route.js - Click Fencing, 2026-09-02
 *
 * Pete: "we have an issue at the calculator for purchases, it goes to book an inspection and
 *        stops sales. please make sure the purchase fence button can go through and people can
 *        buy their fence, add the book free site inspection as an extra option on that screen"
 *
 * ---------------------------------------------------------------------------------------
 * WHAT WAS ACTUALLY HAPPENING - read out of cf-one-price.js, not guessed.
 *
 * On /pages/quote-review, for any quote priced SUPPLY & INSTALL, cf-one-price.js did three
 * things:
 *
 *   1. renamed the primary button to "Book Free Site Inspection ->",
 *   2. hid the delivery date field,
 *   3. and installed this, in installCheckoutHalt():
 *
 *        if (/\/cart\/(clear|add|update)(\.js)?/.test(url) && reviewIsInstall()) {
 *          return new Promise(function () {});      // deliberately never settles
 *        }
 *
 * That third one is the killer. quote-review.js checks out through a promise chain
 * /cart/clear.js -> /cart/add.js -> /cart/update.js -> location = '/checkout'. Stopping the
 * first link with a promise that never settles means the chain simply stops. No error, no
 * message, no redirect - the customer presses the button and NOTHING happens.
 *
 * So a Supply & Install customer could not buy at all. DIY was never affected (the halt tests
 * reviewIsInstall()), which is why it looked like an odd intermittent fault rather than a dead
 * checkout.
 *
 * It was deliberate once: S&I used to mean "book and pay for a site inspection instead". Now
 * that inspections are free, that trade is gone and it just blocks the sale.
 *
 * ---------------------------------------------------------------------------------------
 * WHAT THIS FILE DOES. It makes the inspection an OPTION instead of the only road.
 *
 * The screen now offers both, and the customer picks:
 *
 *     [ Continue to Payment ]                     <- primary, buys the fence
 *     [ Book a free site inspection instead ]     <- secondary, the old behaviour
 *
 * A route flag, window.__cfSiRoute, says which one was pressed. It defaults to 'buy', so the
 * sale is the default path and nothing can silently fall back into the halt.
 *
 * HOW IT DISARMS THE HALT WITHOUT EDITING cf-one-price.js. The halt lives inside that file's
 * own fetch wrapper and keys off reviewIsInstall(), which begins
 * document.getElementById('qr-pricing-label'). Wrapping fetch again from outside cannot get
 * underneath it. So instead of fighting it, this file removes the condition it depends on:
 * while a BUY is in flight it takes the ID off that element, so reviewIsInstall() returns
 * false, the halt does not fire, and the cart chain runs exactly as it does for DIY - through
 * cf-real-line-items.js and cf-delivery-charge-once.js like any other order. The id goes back
 * as soon as the chain has gone quiet.
 *
 * The ID rather than the TEXT, deliberately: the customer sees nothing at all. The element,
 * its wording and its styling are untouched the whole time.
 *
 * That is a blunt instrument and it is written down here so nobody has to rediscover it. It is
 * used because the alternative - bypassing cf-one-price's wrapper by calling a pristine fetch -
 * would ALSO bypass cf-real-line-items.js and cf-delivery-charge-once.js, and the cart would be
 * built from the raw placeholder line with delivery double-charged. Skipping one wrapper in the
 * middle of a chain is not something fetch allows; suppressing the condition is.
 *
 * WHAT IS NOT CHANGED. The inspection route still behaves exactly as it did - day and time
 * fields, the CRM booking with the drawing attached, the confirmation panel, nothing charged.
 * No price, quote, product or cart line is touched by this file.
 *
 * WORTH KNOWING, AND FLAGGED TO PETE: buying a Supply & Install quote charges the INSTALL
 * ESTIMATE, and that figure is only confirmed at the site visit. Both buttons are offered
 * precisely so nobody is forced down either road - which was Pete's original complaint the
 * other way round ("i just went to book a site inspection only and it wanted me to pay the $7k
 * from the fence i just put in").
 */
(function () {
  'use strict';

  if (window.__cfSiRoute0) return;
  window.__cfSiRoute0 = true;

  if (!/\/pages\/quote-review/i.test(location.pathname || '')) return;

  var LABEL_ID = 'qr-pricing-label';
  var BTN_ID = 'qr-enquire-btn';
  var SECOND_ID = 'cf-si-book-btn';
  var STYLE_ID = 'cf-si-route-css';

  /* 'buy' is the default on purpose: if anything below fails, the customer gets the normal
     checkout rather than a dead button. */
  window.__cfSiRoute = window.__cfSiRoute || 'buy';

  function isInstall() {
    var l = document.getElementById(LABEL_ID);
    return !!(l && l.textContent && l.textContent.indexOf('Supply & Install') !== -1);
  }
  function inspecting() { return window.__cfSiRoute === 'inspect'; }

  function css() {
    if (document.getElementById(STYLE_ID)) return;
    var s = document.createElement('style');
    s.id = STYLE_ID;
    /* Pete, 2026-09-02: "why is book a site inspection not lined up with the rest of the
       buttons?" It was display:block with width:100%, so it took a whole row to itself while
       Continue to Payment, Edit sections, Name this plan and Delete Quote sat together in
       one. It is a peer of those buttons, so it now sits in the row with them and inherits
       the shared size and shape from cf-contrast-fixes.liquid - only its colours are set
       here, because those say what it does rather than how big it is.
       The hint keeps its own line: flex-basis:100% makes it wrap below the row instead of
       becoming another item in it, which is what was pushing the row out of line. */
    s.textContent =
      '#' + SECOND_ID + '{background:#fff;color:#1f3a22 !important;' +
      'border:2px solid #2E7D32;cursor:pointer;text-align:center;}' +
      '#' + SECOND_ID + ':hover{background:#f1f9f2;}' +
      '.cf-si-route-hint{flex-basis:100%;width:100%;margin:8px 0 0;font-size:13px;' +
      'line-height:1.45;color:#475569;}';
    (document.head || document.documentElement).appendChild(s);
  }

  /* ---------------------------------------------------------------- the two buttons */

  /* OFFERED ON DIY QUOTES TOO, since 2026-09-02. Pete: "FREE SITE INSPECTION BUTTON on DIY
     quotes" - his screenshot showed the "Ready to proceed?" panel on a DIY quote with no way
     to book from it. A DIY customer who is not sure of their measurements is exactly the
     person a free measure-up wins, so the offer no longer depends on the pricing mode.
     isInstall() is therefore gone from this guard - the button is added for every quote. */
  function addSecondButton() {
    var primary = document.getElementById(BTN_ID);
    if (!primary) return;
    if (document.getElementById(SECOND_ID)) return;

    css();

    var b = document.createElement('button');
    b.type = 'button';
    b.id = SECOND_ID;
    /* Same class as its neighbours so it picks up their shape and spacing rather than
       needing its own. */
    b.className = 'qr-btn';
    b.textContent = 'Book a free site inspection';
    b.addEventListener('click', function (e) {
      e.preventDefault();
      window.__cfSiRoute = 'inspect';
      /* Let cf-one-price paint the inspection version of the modal, then open it. */
      setTimeout(function () { try { primary.click(); } catch (err) {} }, 0);
    });

    var hint = document.createElement('p');
    hint.className = 'cf-si-route-hint';
    hint.textContent = 'Not ready to order? We will come out, measure up and price the job ' +
                       'exactly - completely free, with nothing to pay on the day.';

    primary.parentNode.insertBefore(b, primary.nextSibling);
    b.parentNode.insertBefore(hint, b.nextSibling);
  }

  /* THIS FILE IS NOW THE ONLY THING THAT WRITES THE PRIMARY BUTTON'S LABEL. 2026-09-02.

     Pete: "ive found a flicker ... this book inspection button keeps flickering to continue
     to payment", then "i think we change that button to continue to paymnet and keep the
     button below as book inspection".

     It was this function and cf-one-price.js's syncReview() overwriting each other, each on
     its own 700ms timer - one wrote "Book Free Site Inspection ->", the other wrote it back.
     The label was a race, so pressing it did not reliably mean either thing, and whenever
     cf-one-price won the screen showed two inspection buttons and no way to buy.

     cf-one-price now stands down while window.__cfSiRoute exists (it checks for it), and the
     rule here is Pete's: the primary button ALWAYS reads "Continue to Payment", and booking
     an inspection is the separate button underneath. No condition, no alternation, nothing to
     race - which is what makes the flicker impossible rather than merely unlikely.

     The write is still guarded on a real change, so an unchanged label costs no DOM mutation
     and the observers watching this page stay quiet. */
  function keepPrimaryAsBuy() {
    var p = document.getElementById(BTN_ID);
    if (!p) return;
    var want = 'Continue to Payment';
    if (p.textContent.trim() !== want) p.textContent = want;
  }

  /* Leaving the modal without booking must put the route back to 'buy'. Otherwise a customer
     who opened the inspection form, changed their mind and closed it would still be flagged
     as inspecting - which keeps cf-one-price's day/time fields on screen and, worse, keeps the
     checkout halt armed so their next press of Continue to Payment would do nothing.
     Once the booking has actually gone through (#cf-si-booked is on screen) the route is left
     alone, because the halt still has to hold the page still underneath the confirmation. */
  function releaseRouteIfModalClosed() {
    if (!inspecting()) return;
    if (document.getElementById('cf-si-booked')) return;
    var form = document.getElementById('qr-enquire-form');
    if (form && form.offsetParent === null) window.__cfSiRoute = 'buy';
  }

  /* Pressing the primary button always means "buy". */
  document.addEventListener('click', function (e) {
    var t = e.target;
    if (!t || !t.closest) return;
    if (t.closest('#' + SECOND_ID)) return;
    if (t.closest('#' + BTN_ID)) window.__cfSiRoute = 'buy';
  }, true);

  /* ------------------------------------------------- disarming the checkout halt */

  /* The id is taken off rather than the text changed. reviewIsInstall() starts with
     document.getElementById('qr-pricing-label'), so a missing id makes it return false just as
     surely - and unlike rewriting the words, the customer sees absolutely nothing. The element,
     its text and its styling are untouched throughout. */
  var patched = false;
  var patchedEl = null;

  function suppress() {
    if (patched) return;
    var l = document.getElementById(LABEL_ID);
    if (!l) return;
    if (String(l.textContent || '').indexOf('Supply & Install') === -1) return;
    patchedEl = l;
    patched = true;
    l.setAttribute('data-cf-was-pricing-label', '1');
    l.removeAttribute('id');
  }

  function restore() {
    if (!patched) return;
    var l = patchedEl || document.querySelector('[data-cf-was-pricing-label="1"]');
    if (l) {
      l.id = LABEL_ID;
      l.removeAttribute('data-cf-was-pricing-label');
    }
    patched = false;
    patchedEl = null;
  }

  /* The chain is /cart/clear.js -> /cart/add.js -> /cart/update.js -> location='/checkout', and
     the links are seconds apart on a slow connection. Restoring a fixed time after the FIRST
     call would put the id back mid-chain and re-arm the halt on a later link, which would hang
     the checkout in a different place. So the restore is debounced off the LAST cart call. */
  var CART_RE = /\/cart\/(clear|add|update)(\.js)?/;
  var restoreTimer = null;
  var lastCartAt = 0;

  function scheduleRestore() {
    if (restoreTimer) clearTimeout(restoreTimer);
    restoreTimer = setTimeout(function () {
      restoreTimer = null;
      restore();
    }, 2500);
  }

  /* ORDER IS LOAD-BEARING. Our wrapper has to sit OUTSIDE cf-one-price's, so that we can take
     the id off before its halt looks for it. Wrapping at load time would be a coin toss on
     script order, so instead we wait until it has actually installed (it sets
     __cfOnePriceInstalled at the end of its own boot) and only then capture window.fetch.
     The fallback timeout means a build that no longer ships cf-one-price still gets the two
     buttons and a working checkout rather than nothing at all. */
  var wrapped = false;

  function wrapFetch() {
    if (wrapped) return;
    var origFetch = window.fetch;
    if (typeof origFetch !== 'function') return;
    wrapped = true;

    window.fetch = function (input, init) {
      var url = '';
      try { url = String((input && input.url) || input || ''); } catch (e) {}

      /* Act only on the buy route, and only on the cart chain. Once suppressed, isInstall()
         reads false for the rest of the chain, so `patched` is what keeps it going. */
      if (CART_RE.test(url) && !inspecting() && (patched || isInstall())) {
        lastCartAt = new Date().getTime();
        try { suppress(); } catch (e) {}
        var p;
        try {
          p = origFetch.apply(this, arguments);
        } catch (e) {
          scheduleRestore();
          throw e;
        }
        return Promise.resolve(p).then(function (r) {
          scheduleRestore();
          return r;
        }, function (err) {
          scheduleRestore();
          throw err;
        });
      }

      return origFetch.apply(this, arguments);
    };

    try { console.log('[cf-si-route] fetch wrapped outside cf-one-price'); } catch (e) {}
  }

  (function waitForOnePrice() {
    var tries = 0;
    (function poll() {
      if (window.__cfOnePriceInstalled) { wrapFetch(); return; }
      if (++tries > 120) { wrapFetch(); return; }   /* ~12s, then go anyway */
      setTimeout(poll, 100);
    })();
  })();

  /* Safety net: never leave the id off if a chain dies somewhere unexpected. Only fires once
     the cart has genuinely been quiet, so it can never cut a slow checkout short. */
  setInterval(function () {
    if (patched && new Date().getTime() - lastCartAt > 15000) restore();
  }, 5000);

  function tick() {
    try {
      addSecondButton();
      keepPrimaryAsBuy();
      releaseRouteIfModalClosed();
    } catch (e) {}
  }

  function start() {
    tick();
    try {
      if (window.MutationObserver) {
        var t = null;
        new MutationObserver(function () {
          if (t) clearTimeout(t);
          t = setTimeout(tick, 120);
        }).observe(document.body, { childList: true, subtree: true, characterData: true });
      }
    } catch (e) {}
    setInterval(tick, 700);
    try { console.log('[cf-si-route] installed - purchase enabled, inspection optional'); } catch (e) {}
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start);
  else start();
})();
